import { useState } from "react"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { PageMeta } from "@/components/shared/PageMeta"
import CatalogCard from "@/components/shared/CatalogCard"
import BuyNowButton from "@/features/checkout/BuyNowButton"
import TREATMENTS, {
	type TreatmentId,
	type TreatmentProps,
	type TreatmentWeeks,
	treatmentSlugs,
	treatmentCatalogId
} from "@/content/treatments"
import { getDisplayPrice, getTreatmentItem, promotionFromActive } from "@/content/prices"
import { PAGE_SEO } from "@/lib/seo"
import { imageBaseURL } from "@/lib/utils"
import { useActivePromotionForItem } from "@/features/promotions/ActivePromotionProvider"

const GALLERY_SLUG_OVERRIDES: Partial<Record<TreatmentId, string>> = {
	"diabetes-tipo-2": "diabetes",
	"envejecimiento-prematuro": "envejecimiento"
}

function treatmentGalleryImage(slug: TreatmentId): string {
	const file = GALLERY_SLUG_OVERRIDES[slug] ?? slug
	return imageBaseURL(`treatments/gallery/${file}.webp`)
}

const EAGER_IMAGE_COUNT = 3

const SYMPTOM_COPY = {
	four: "Para síntomas severos y crónicos",
	two: "Para síntomas leves y moderados"
} as const satisfies Record<TreatmentWeeks, string>

interface TreatmentCardProps {
	treatment: TreatmentProps
	index: number
	showComplete: boolean
}

function TreatmentCard({ treatment, index, showComplete }: TreatmentCardProps): React.JSX.Element {
	const duration: TreatmentWeeks = showComplete ? "four" : "two"
	const item = getTreatmentItem(treatment.id, duration)
	const catalogId = treatmentCatalogId(treatment.id, duration)
	const active = useActivePromotionForItem(catalogId)
	const promo = promotionFromActive(active)
	const price = getDisplayPrice(item.price, promo, catalogId)
	const hasFreeShipping = Boolean(active?.freeShipping && active.itemIds.includes(catalogId))
	const cartItem = { id: treatment.id, priceId: item.priceId, amount: 1, duration }

	return (
		<CatalogCard
			href={`/tratamientos/${treatment.id}`}
			imageSrc={treatmentGalleryImage(treatment.id)}
			eager={index < EAGER_IMAGE_COUNT}
			title={treatment.name}
			subtitle={SYMPTOM_COPY[duration]}
			price={price}
			hasFreeShipping={hasFreeShipping}
			buyButton={
				<BuyNowButton
					cartItem={cartItem}
					text="Comprar"
					variant="default"
					size="sm"
					className="ml-auto shrink-0 cursor-pointer"
				/>
			}
		/>
	)
}

export default function TratamientosPage(): React.JSX.Element {
	const [showComplete, setShowComplete] = useState(true)

	return (
		<>
			<PageMeta {...PAGE_SEO.tratamientos} path="/tratamientos" />
			<div className="mx-auto max-w-6xl px-4 py-12">
				<div className="mb-8 flex flex-wrap items-center justify-between gap-4">
					<h1 className="text-3xl font-bold">Tratamientos</h1>
					<div className="flex items-center gap-3">
						<Switch id="plan-complete-toggle" checked={showComplete} onCheckedChange={setShowComplete} />
						<Label htmlFor="plan-complete-toggle" className="cursor-pointer text-base">
							Completo
						</Label>
					</div>
				</div>
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{treatmentSlugs.map((slug, index) => (
						<TreatmentCard key={slug} treatment={TREATMENTS[slug]} index={index} showComplete={showComplete} />
					))}
				</div>
			</div>
		</>
	)
}
